import { useCallback, useEffect, useRef, useState } from 'react'
import apiService from '../services/api'
import {
  KEEP_POINTS_VALUE,
  REWARD_INVALIDATED_MESSAGE,
  formatCaisseRewardConfirmMessage,
  shouldInvalidateConfirmedAfterQuoteResult,
} from '../utils/caisseFidelite'

const DEBOUNCE_MS = 350

/**
 * F2.4b1–b2b / F2.5a3 — Catalogue + choix + confirmation.
 * Sur ticket ouvert : sync intention via PATCH (0 apply jusqu’à encaissement).
 * Quote : reductionPromo réel (base % = sousTotal − promo, vérité serveur).
 */
export function useCaisseFidelite({
  active,
  commercantId,
  clientUserId,
  sousTotal,
  reductionPromo = 0,
  ticketId = null,
  expectedPosRevision = null,
  onPosRevisionUpdated = null,
}) {
  const [catalogue, setCatalogue] = useState(null)
  const [catalogueLoading, setCatalogueLoading] = useState(false)
  const [catalogueError, setCatalogueError] = useState(null)
  const [selectedRewardId, setSelectedRewardId] = useState(null)
  const [confirmedRewardId, setConfirmedRewardId] = useState(null)
  const [quote, setQuote] = useState(null)
  const [quoteLoading, setQuoteLoading] = useState(false)
  const [quoteError, setQuoteError] = useState(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [pendingRewardId, setPendingRewardId] = useState(null)
  const [notice, setNotice] = useState(null)
  const [intentSyncing, setIntentSyncing] = useState(false)

  const requestIdRef = useRef(0)
  const guardRef = useRef(false)
  const confirmedRef = useRef(null)
  const debounceRef = useRef(null)

  useEffect(() => {
    confirmedRef.current = confirmedRewardId
  }, [confirmedRewardId])

  const syncTicketIntent = useCallback(
    async (rewardId) => {
      if (!ticketId || !commercantId) return
      if (expectedPosRevision == null) return
      setIntentSyncing(true)
      try {
        const res = await apiService.patchTicketFideliteIntent(ticketId, {
          rewardId: rewardId || null,
          commercantId,
          expectedPosRevision,
        })
        const nextRev = res?.ticket?.posRevision
        if (nextRev != null && typeof onPosRevisionUpdated === 'function') {
          onPosRevisionUpdated(nextRev)
        }
      } finally {
        setIntentSyncing(false)
      }
    },
    [ticketId, commercantId, expectedPosRevision, onPosRevisionUpdated]
  )

  const resetSelection = useCallback(() => {
    setSelectedRewardId(null)
    setConfirmedRewardId(null)
    setQuote(null)
    setQuoteError(null)
    setConfirmOpen(false)
    setPendingRewardId(null)
  }, [])

  const clearAll = useCallback(() => {
    resetSelection()
    setCatalogue(null)
    setCatalogueError(null)
    setNotice(null)
  }, [resetSelection])

  const loadCatalogue = useCallback(async () => {
    if (!active || !commercantId || !clientUserId) {
      clearAll()
      return null
    }
    setCatalogueLoading(true)
    setCatalogueError(null)
    try {
      const data = await apiService.getCaisseFideliteCatalogue(clientUserId, commercantId)
      setCatalogue(data)
      return data
    } catch (err) {
      setCatalogue(null)
      setCatalogueError(err?.message || 'Impossible de charger les récompenses')
      resetSelection()
      return null
    } finally {
      setCatalogueLoading(false)
    }
  }, [active, commercantId, clientUserId, clearAll, resetSelection])

  useEffect(() => {
    if (!active || !clientUserId) {
      clearAll()
      return undefined
    }
    loadCatalogue()
    return undefined
  }, [active, clientUserId, commercantId, loadCatalogue, clearAll])

  const prevClientRef = useRef(clientUserId)
  useEffect(() => {
    if (prevClientRef.current && prevClientRef.current !== clientUserId) {
      resetSelection()
      setNotice(
        clientUserId
          ? 'Client modifié : la récompense précédente a été annulée.'
          : REWARD_INVALIDATED_MESSAGE
      )
    }
    prevClientRef.current = clientUserId
  }, [clientUserId, resetSelection])

  const fetchQuote = useCallback(
    async (rewardId) => {
      if (!clientUserId || !rewardId || !commercantId) {
        setQuote(null)
        return null
      }
      const reqId = ++requestIdRef.current
      setQuoteLoading(true)
      setQuoteError(null)
      try {
        const data = await apiService.postCaisseFideliteQuote({
          clientUserId,
          rewardId,
          sousTotal: Number(sousTotal) || 0,
          reductionPromo: Number(reductionPromo) || 0,
          fraisLivraison: 0,
          commercantId,
        })
        if (reqId !== requestIdRef.current) return null
        setQuote(data)
        if (!data?.applicable) {
          setQuoteError('Cette récompense n’est pas applicable sur cette vente.')
        }
        return data
      } catch (err) {
        if (reqId !== requestIdRef.current) return null
        setQuote(null)
        setQuoteError(err?.message || 'Impossible de vérifier la récompense')
        return { __error: true, err }
      } finally {
        if (reqId === requestIdRef.current) setQuoteLoading(false)
      }
    },
    [clientUserId, commercantId, sousTotal, reductionPromo]
  )

  const clearConfirmedAfterInvalidate = useCallback(
    async (message = REWARD_INVALIDATED_MESSAGE) => {
      try {
        await syncTicketIntent(null)
      } catch {
        /* intention locale prioritaire pour bloquer soft-skip */
      }
      resetSelection()
      setNotice(message || REWARD_INVALIDATED_MESSAGE)
      await loadCatalogue()
    },
    [syncTicketIntent, resetSelection, loadCatalogue]
  )

  // Re-quote quand panier / promo change et qu’une récompense est confirmée → invalider si morte
  useEffect(() => {
    if (!confirmedRewardId || !active || !clientUserId) return undefined
    const rewardAtStart = confirmedRewardId
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      const q = await fetchQuote(rewardAtStart)
      if (confirmedRef.current !== rewardAtStart) return
      const err = q && q.__error ? q.err : null
      const quoteOk = q && !q.__error ? q : null
      if (shouldInvalidateConfirmedAfterQuoteResult(quoteOk, err)) {
        await clearConfirmedAfterInvalidate(REWARD_INVALIDATED_MESSAGE)
      }
    }, DEBOUNCE_MS)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [
    confirmedRewardId,
    active,
    clientUserId,
    sousTotal,
    reductionPromo,
    fetchQuote,
    clearConfirmedAfterInvalidate,
  ])

  const selectKeepPoints = useCallback(async () => {
    try {
      await syncTicketIntent(null)
      setSelectedRewardId(null)
      setConfirmedRewardId(null)
      setQuote(null)
      setQuoteError(null)
      setConfirmOpen(false)
      setPendingRewardId(null)
      setNotice(null)
    } catch (err) {
      setNotice(err?.message || 'Impossible de retirer l’intention')
    }
  }, [syncTicketIntent])

  const requestSelectReward = useCallback(
    async (rewardId) => {
      if (!rewardId || guardRef.current) return
      const row = (catalogue?.recompenses || []).find((r) => r.rewardId === rewardId)
      if (!row || !row.accessible) return
      guardRef.current = true
      setNotice(null)
      try {
        setPendingRewardId(rewardId)
        setSelectedRewardId(rewardId)
        const q = await fetchQuote(rewardId)
        const quoteOk = q && !q.__error ? q : null
        if (quoteOk?.applicable) {
          setConfirmOpen(true)
        } else {
          setPendingRewardId(null)
          setSelectedRewardId(confirmedRef.current)
        }
      } finally {
        guardRef.current = false
      }
    },
    [catalogue, fetchQuote]
  )

  const cancelConfirm = useCallback(() => {
    setConfirmOpen(false)
    setPendingRewardId(null)
    setSelectedRewardId(confirmedRewardId)
    if (!confirmedRewardId) {
      setQuote(null)
      setQuoteError(null)
    }
  }, [confirmedRewardId])

  const acceptConfirm = useCallback(async () => {
    if (!pendingRewardId || !quote?.applicable) {
      cancelConfirm()
      return
    }
    try {
      await syncTicketIntent(pendingRewardId)
      setConfirmedRewardId(pendingRewardId)
      setSelectedRewardId(pendingRewardId)
      setConfirmOpen(false)
      setPendingRewardId(null)
      setNotice(null)
    } catch (err) {
      setNotice(err?.message || 'Impossible d’enregistrer l’intention')
      cancelConfirm()
    }
  }, [pendingRewardId, quote, cancelConfirm, syncTicketIntent])

  const recompenses = Array.isArray(catalogue?.recompenses) ? catalogue.recompenses : []
  const showPanel =
    !!active &&
    !!clientUserId &&
    !!catalogue?.enabled &&
    catalogue?.programmeActif === true

  const pendingReward = pendingRewardId
    ? recompenses.find((r) => r.rewardId === pendingRewardId)
    : null
  const confirmedReward = confirmedRewardId
    ? recompenses.find((r) => r.rewardId === confirmedRewardId)
    : null

  const clientLabel = catalogue?.client
    ? [catalogue.client.prenom, catalogue.client.nom].filter(Boolean).join(' ')
    : null

  const confirmMessage = formatCaisseRewardConfirmMessage(quote, pendingReward, clientLabel)

  return {
    showPanel,
    catalogueLoading,
    catalogueError,
    recompenses,
    solde: catalogue?.solde ?? 0,
    client: catalogue?.client || null,
    selectedRewardId,
    confirmedRewardId,
    confirmedReward,
    quote,
    quoteLoading: quoteLoading || intentSyncing,
    quoteError,
    confirmOpen,
    pendingReward,
    confirmMessage,
    notice,
    KEEP_POINTS_VALUE,
    selectKeepPoints,
    requestSelectReward,
    cancelConfirm,
    acceptConfirm,
    dismissNotice: () => setNotice(null),
    resetSelection,
    clearAll,
    hydrateIntent: (rewardId) => {
      if (rewardId) {
        setConfirmedRewardId(rewardId)
        setSelectedRewardId(rewardId)
      }
    },
  }
}

export default useCaisseFidelite
